import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { baseQueryConfig } from "../api";
import {
  IUpdateUserRequest,
  IUser,
  IUserWithMeta,
  getToken,
} from "../models/users.models";

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const usersConfig = {
  ...baseQueryConfig,
  baseUrl: baseQueryConfig.baseUrl + "/account",
  prepareHeaders: async (headers: Headers) => {
    let session = getToken();
    if (!session || !session.token) {
      await delay(500);
      session = getToken();
    }

    if (session && session.token) {
      headers.set("Authorization", `Bearer ${session.token}`);
    }

    return headers;
  },
};

export const usersApi = createApi({
  reducerPath: "usersApi",
  tagTypes: ["Users", "Profiles"],
  baseQuery: fetchBaseQuery(usersConfig),
  endpoints: (builder) => ({
    getUsers: builder.query<
      IUserWithMeta,
      {
        page?: number;
        limit?: number;
        email?: string;
        uid?: string;
      }
    >({
      query: ({ page, limit, email, uid }) => ({
        url: "/search/params",
        params: {
          page,
          limit,
          email,
          uid,
        },
      }),
      providesTags: ["Users", "Profiles"],
    }),
    getSubscribedUsers: builder.query<IUser[], {}>({
      query: () => ({
        url: "/search/subscribed",
        params: {},
      }),
      providesTags: ["Users", "Profiles"],
    }),
    updateUser: builder.mutation<void, IUpdateUserRequest>({
      query: ({ uid, role, expirablePoints }) => ({
        url: `/${uid}`,
        method: "PUT",
        body: { role, expirablePoints },
      }),
      invalidatesTags: ["Users"],
    }),
    deleteUser: builder.mutation<boolean, string>({
      query: (uid) => ({
        url: `/${uid}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Users"],
    }),
  }),
});

export const {
  useUpdateUserMutation,
  useGetUsersQuery,
  useGetSubscribedUsersQuery,
  useDeleteUserMutation,
} = usersApi;
